/** Shared schedule/delivery field constants for TaskConfigPanel and InstallDialog. */

export const TIMEZONE_OPTS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
] as const

export const SCHEDULE_OPTIONS = [
  { value: 'once', label: 'One-time' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
] as const

export const INSTALL_SCHEDULE_OPTIONS = SCHEDULE_OPTIONS.filter((opt) => opt.value !== 'once')

export const DATE_RANGE_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  once: [
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'last_30_days', label: 'Last 30 days' },
    { value: 'previous_month', label: 'Previous calendar month' },
  ],
  hourly: [
    { value: 'last_hour', label: 'Last hour' },
    { value: 'previous_hour', label: 'Previous clock hour' },
  ],
  daily: [
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_24_hours', label: 'Last 24 hours' },
  ],
  weekly: [
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'previous_week', label: 'Previous week (Mon–Sun)' },
  ],
  monthly: [
    { value: 'last_30_days', label: 'Last 30 days' },
    { value: 'previous_month', label: 'Previous calendar month' },
  ],
  quarterly: [
    { value: 'last_90_days', label: 'Last 90 days' },
    { value: 'previous_quarter', label: 'Previous calendar quarter' },
  ],
}

/** Indexed Sun=0 … Sat=6, matching `day_of_week` in schedule_config. */
export const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/** Monday-first select options used by TaskConfigPanel. */
export const WEEKDAY_SELECT: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

export const inputClass =
  'tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-800 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/30 focus:tw-border-blue-500 tw-transition-colors'

export const labelClass = 'tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5'

export const sectionClass = 'tw-bg-slate-50/80 tw-rounded-xl tw-p-3 tw-border tw-border-slate-100'

export function tomorrowYmd(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function todayYmd(): string {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function dateRangeOptionsFor(scheduleType: string): Array<{ value: string; label: string }> {
  return DATE_RANGE_OPTIONS[scheduleType] || DATE_RANGE_OPTIONS.weekly
}

export function dateRangeHumanLabel(strategy?: string | null): string | null {
  if (!strategy) return null
  for (const opts of Object.values(DATE_RANGE_OPTIONS)) {
    const match = opts.find((opt) => opt.value === strategy)
    if (match) return match.label
  }
  return strategy.replace(/_/g, ' ')
}
